import React from 'react';
import { 
  Box, 
  Typography,
  Link,
  List,
  ListItem,
  styled
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigationBar from './NavigationBar';
import SiteFooter from './SiteFooter';
import LegalDocLayout from './LegalDocLayout';

// Styled Components
const ContentSection = styled(Box)({
  marginBottom: '32px',
});

const StyledLink = styled(Link)({
  color: '#4F46E5',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const PageWrapper = styled(Box)(({ theme }) => ({
    backgroundColor: '#F8F9FA',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  }));

// Bold span used for defined terms and list lead-ins
const Bold = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 600 }}>{children}</Box>
);

// In-site links
const TermsLink = () => (
  <StyledLink component={RouterLink} to="/terms-of-service">Terms of Service</StyledLink>
);

// Bulleted list (MUI's default List renders no bullet markers)
const BulletList = ({ children }) => (
  <List sx={{ listStyleType: 'disc', pl: 4 }}>
    {children}
  </List>
);

const BulletItem = ({ children }) => (
  <ListItem sx={{ display: 'list-item', px: 0, py: 0.5 }}>{children}</ListItem>
);

// Lettered (a., b., c.) list used in the Rights and Choices section
const LetteredList = ({ children }) => (
  <Box component="ol" sx={{ listStyleType: 'lower-alpha', pl: 4, mt: 0, mb: 2 }}>
    {children}
  </Box>
);

const LetteredItem = ({ children }) => (
  <Typography component="li">{children}</Typography>
);

function PrivacyNotice() {
  return (
    <PageWrapper>
      <NavigationBar />
      
      <LegalDocLayout>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 700 }}>
          EDUCATION DATA COLLECTIVE, LLC
        </Typography>
        <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ fontWeight: 600 }}>
        PRIVACY NOTICE
        </Typography>
        <Typography variant="subtitle1" gutterBottom align="center">
        <Box component="span" sx={{ fontWeight: 'bold' }}>Last Updated</Box>: October 19, 2026
        </Typography>

        <ContentSection>
        <Typography paragraph>
            This Privacy Notice (the “<Bold>Privacy Notice</Bold>”) describes the privacy and data protection practices of Education Data
            Collective, LLC (“<Bold>EDC</Bold>,” “<Bold>we</Bold>,” “<Bold>us</Bold>,” or “<Bold>our</Bold>”). This Privacy Notice is intended to provide details regarding the information
            we collect and/or process through our website located at{' '}
            <StyledLink href="https://www.educationdatacollective.com/" target="_blank" rel="noopener">
            https://www.educationdatacollective.com/
            </StyledLink>
            {' '}or any successor website (the “<Bold>Site</Bold>”), and all other applications, products, services, features, tools, application programming interfaces
            and software that we own or control and which post or link to this Privacy Notice (collectively, the “<Bold>Services</Bold>”), or
            through other communication methods such as email or phone. This Privacy Notice is governed by our <TermsLink />. Defined terms used herein and not otherwise defined have the meanings set forth in the <TermsLink />.
        </Typography>

        <Typography paragraph sx={{ fontWeight: 'bold' }}>
            PLEASE READ THIS PRIVACY NOTICE CAREFULLY TO UNDERSTAND HOW WE HANDLE YOUR PERSONAL INFORMATION. IF
            YOU DO NOT AGREE TO THIS PRIVACY NOTICE, PLEASE DO NOT ACCESS THE SITE OR OTHERWISE USE OUR SERVICES.
        </Typography>

        <Typography paragraph>
            By accessing or using the Site or other Services, you agree to this Privacy Notice. This Privacy Notice may change from time to time (see Changes to Our Privacy Notice below). Your continued use of the Site or the Services after we make changes is deemed to be acceptance of those changes, so please check the Privacy Notice periodically for updates.
        </Typography>
        </ContentSection>

        {/* Section 1: Scope of our Privacy Notice */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            1. Scope of our Privacy Notice
        </Typography>
        <Typography paragraph>
            The amount and type of information we collect about you will depend upon the Services you use and the information
            you choose to provide. Please note that we need certain types of information so that you can access our Services or so
            we can provide the Services you request. If you do not provide us with the required information or ask us to delete it,
            you may no longer be able to access or use our Services. For purposes of this Privacy Notice, personal information means information relating directly or indirectly to an identifiable individual, except as may be set forth in applicable law.
        </Typography>

        <Typography paragraph>
            All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any
            changes to such personal information. You may not provide us information that is obscene, defamatory, infringing,
            malicious, or that violates any law. If you provide personal information of a third party, you are responsible for providing any notices and obtaining any consents necessary for us to collect and use such personal information as described in this Privacy Notice.
        </Typography>

        <Typography paragraph>
            The Services are intended for use by educational support organizations, schools, school districts and other education institutions (“<Bold>Organization Customers</Bold>”) and the individual schools, teachers and administrators they support (“<Bold>Customer Users</Bold>”). EDC respects the privacy of the student educational records and personal information provided to us by our Organization Customers to process on their behalf (“<Bold>Customer Data</Bold>”) when they, their staff or other Customer Users access and use the Services. Customer Data is the data received by EDC from its Organization Customers and their Customer Users and may include student addresses, directory information, and other student information. Customer Data is processed by EDC on behalf of each Organization Customer in our capacity as a “data processor”.
        </Typography>

        <Typography paragraph>
            EDC complies with applicable privacy laws of the United States with respect to personal information and student educational records. We do not use Customer Data for any purpose other than to provide the Services, in accordance with our contractual agreements with our Organization Customers, our <TermsLink />, and this Privacy Notice. EDC does not own or control Customer Data, which belongs to the student and/or the Organization Customer that contracts with EDC to provide access to the Services. EDC processes the Customer Data under strict contractual obligations and privacy agreements signed with the Organization Customer. EDC does not use or disclose Customer Data except as authorized and required by our Organization Customers and as provided for in our agreements with our Organization Customers. Because EDC does not own or control Customer Data, any request for access, review, correction, or deletion of Customer Data must come directly from our Organization Customers. EDC is not responsible for the privacy practices of its Organization Customers.
        </Typography>
        </ContentSection>

        {/* Section 2: Information We Collect */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            2. Information We Collect
        </Typography>
        <Typography paragraph>
            We may also collect information about you for our own use when you visit the Site and as more fully described below. In these situations, EDC is a “data controller” because EDC determines the purposes for which and the way the personal information is processed.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Information You Provide to Us
        </Typography>
        <Typography paragraph>
            It’s probably no surprise that we collect information that you provide to us. For example, we collect information from you
            when:
        </Typography>
        <BulletList>
            <BulletItem>you request information from us or otherwise communicate with us;</BulletItem>
            <BulletItem>an account is created by you or on your behalf;</BulletItem>
            <BulletItem>you carry out transactions through our Site and Services;</BulletItem>
            <BulletItem>you request customer support or technical assistance with the Services; and</BulletItem>
            <BulletItem>you complete any forms or respond to a survey that we might ask you to complete for research purposes.</BulletItem>
        </BulletList>
        <Typography paragraph>
            This information may include:
        </Typography>
        <BulletList>
            <BulletItem>name, phone number and email address;</BulletItem>
            <BulletItem>postal address;</BulletItem>
            <BulletItem>employment-related information, including, but not limited to, job title and employer; and</BulletItem>
            <BulletItem>financial and payment information.</BulletItem>
        </BulletList>

        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Information We Collect Through Our Services
        </Typography>
        <Typography paragraph>
            We may collect personal information provided by you, your employer or other users of the Services that may be required to use the Services. We expect all users to follow their organization’s privacy policy and any applicable regulatory requirements when providing personal information for use of the Services.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Information We Collect Automatically
        </Typography>
        <Typography paragraph>
            When you use the Services, we collect and analyze some information via a variety of technologies that automatically collect information about the Services. This information includes, but is not limited to, certain details of your access to and use, including traffic data, location data, logs, and other communication data and the resources that you access and use on or through the Services. We use this information to track the use of the Site and other Services and to assist us in maintaining, evaluating, and improving the Services.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Information We Collect from Other Sources
        </Typography>
        <Typography paragraph>
            We may receive personal information about you from third-party sources such as your employer, marketing partners, social media and other cloud-based platforms, publicly available sources, data providers, or third-party staffing or personnel providers. This Privacy Notice does not address, and we are not responsible for, the privacy, information, or other practices of any third parties, including any third party operating any website or service to which the Site links. The inclusion of a link on the Site does not imply endorsement of the linked site or service by us or by our affiliates.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Recruiting and Candidate Information
        </Typography>
        <Typography paragraph>
            If you choose to apply to work with or for us through our Site or by otherwise contacting us regarding opportunities to become an employee, agent, or contractor for us, we may collect the following:
        </Typography>
        <List>
            <ListItem>
            <Typography>
                <Bold>Identification data,</Bold> such as your name, date of birth, social security number, and other government identifiers.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>Contact information,</Bold> such as your first and last name, email and mailing addresses, phone number, emergency contact information, professional title or profession, and company name, if applicable.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>Application information</Bold> regarding qualifications, skills, employment or education history, or other related information provided voluntarily in response to questions asked during any interviews.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>Reference information</Bold>, such as name and contact details of your references.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>Social media information,</Bold> such as if you provide us a link to or other access to a social media account, we may collect or access any information you permit to be shared through or from your social media account and other information depending on the social media platform.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>Background check information</Bold>, subject to applicable law.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>Compensation information,</Bold> such as financial account information, and any other compensation information that you voluntarily provide, which may depend on your region and applicable laws and regulations.
            </Typography>
            </ListItem>
        </List>
        <Typography paragraph>
            You are responsible for the accuracy of the personal information you provide or make available to us. Some information is provided directly to us for internal use, while other information may be shared directly with a service provider who handles specific aspects of our operations. Some information is mandatory to provide because it enables us to perform mandatory employee-hiring processes, like issuing your paycheck or verifying your eligibility for employment. All personal information you provide, particularly in connection with our recruiting activities, must be truthful, accurate and not misleading in any way.
        </Typography>
        </ContentSection>

        {/* Section 3: Cookies and Similar Technologies */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            3. Cookies and Similar Technologies
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Cookies
        </Typography>
        <Typography paragraph>
            Like many organizations, we collect information about how you use our Site by setting and accessing cookies on your
            device. A cookie is a small piece of information sent by our Site that is saved on your storage drive by your computer’s
            browser. The cookie holds information our Site may need to personalize or enhance your experience and to gather statistical data, such as which pages are visited, the Internet provider’s domain name and the addresses of the site visited immediately before coming to and immediately after leaving our Site. We may link the anonymous visitor ID from your cookie to a user ID in our database to help us analyze web traffic and statistics. We may use third-party analytics tools such as Google Analytics, and, from time to time, other companies may help us with data research and analysis, but they will be prohibited from using that data for any other purpose. You may disable cookies on your browser. Please review your browser’s instructions for doing so. Note that certain features of the Site may not be available if you delete or reject cookies.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Pixel Tags
        </Typography>
        <Typography paragraph>
            We may use pixel tags (also called web beacons or clear gifs) on our Site and emails that permit us, for example, to count users who have visited specific pages or opened an email, and for other related website and app statistics (for example, recording the popularity of certain app content and verifying system and server integrity). They can also help us customize your browsing experience. We may use information collected through pixel tags or tracked links in combination with your personal information. We may also combine personal information you provide to us with other personal information (such as demographic information). If we work with other companies to help us track, collect and analyze this information, they will be prohibited from using this information for any other purpose.
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Other Technologies
        </Typography>
        <Typography paragraph>
            The Site and Services, including our service providers, may also use other technologies such as JavaScript. We may use local storage (HTML5). We may also permit third parties to use cookies, web beacons, JavaScript and eTags on our Site.
        </Typography>
        </ContentSection>

        {/* Section 4: How We Use Your Personal Information */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            4. How We Use Your Personal Information
        </Typography>
        <Typography paragraph>
            We, or our service providers working on our behalf, may use your personal information for various purposes depending
            on the types of information we have collected from you in order to:
        </Typography>
        <List>
            <ListItem>
            <Typography>
                Provide you with the Services. We may also use such information you provide in aggregated or deidentified
                form, to refine and improve the Services.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                Fulfill any other purpose for which you provide it.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                Process transactions.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                Give you notices about your account and renewals.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                Notify you when updates to the Services are available, and of enhancements or changes to any products or
                services we offer.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                Respond to your requests for information and provide you with more effective and efficient customer
                service.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                Market, promote, and conduct advertising for the Services. For example, we use your information to send
                you marketing communications via email or other contact methods where permitted by applicable law.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                Maintain, secure, and improve current and future Services. For example, our Services may collect your
                information through forms that you submit and may collect information automatically about your use of the
                Services to inform us about Services performance, areas of improvement, and Services updates and changes
                we may decide to evaluate.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                Comply with any procedures, laws, and regulations which apply to us or our processing. For example, we comply with subpoenas and other court orders to process data where we have determined there is a legal requirement for us to do so.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                Establish, exercise, or defend our legal rights. We may process your personal information in connection with establishing, exercising, or defending our legal rights where it is necessary for our legitimate interests or the legitimate interests of others or for fraud prevention. For example, we may process information you provide to identify any fraudulent, harmful, unauthorized, unethical, or illegal activity, such as use of another individual’s identity, and we may use your information as necessary to defend ourselves in litigation or enforce our rights or agreements with others.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                Conduct outreach, recruiting and hiring activities for opportunities with us. If you apply to work with or for us, we will review the information you submit to determine if your interest, qualifications and experience match any available opportunities with us, to verify the information you provide, communicate with you regarding any opportunities, improve our recruiting processes, process your onboarding if hired, and comply with applicable labor and employment laws.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                Other legitimate purposes as required or permitted by applicable law. We may process your information for other purposes consistent with those in this Privacy Notice and as may be permitted or required by applicable law.
            </Typography>
            </ListItem>
        </List>
        </ContentSection>

        {/* Section 5: Aggregate/Deidentified Data */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            5. Aggregate/Deidentified Data
        </Typography>
        <Typography paragraph>
            We may aggregate and/or deidentify any information collected through the Services so that the information can no
            longer be linked to you or your device. We may use the aggregated and/or deidentified information for any purpose,
            including without limitation for research purposes and to promote the Services, and may also share such data with any
            third parties, including advertisers, promotional partners, and sponsors.
        </Typography>
        </ContentSection>

        {/* Section 6: How We Share and Disclose Your Personal Information */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            6. How We Share and Disclose Your Personal Information
        </Typography>
        <Typography paragraph>
            We do not sell your personal information to third parties or share your personal information with third parties for cross-context behavioral advertising purposes.
        </Typography>
        <Typography paragraph>
            We may share your personal information in the following ways:
        </Typography>
        <List>
            <ListItem>
            <Typography>
                <Bold>Third-Party Service Providers:</Bold> We may provide access to or share your information with select third parties
                who perform services on our behalf as a service provider. They provide a variety of services to us, including
                data storage and/or processing, website and app hosting and operation, surveys, security, fraud prevention,
                billing, payment processing, marketing and advertising, and legal services. Third-party service providers may have access to your personal information to perform these services but are prohibited from using your information for other purposes. We will enter into data protection agreements with service providers that have access to your personal information, which will prohibit them from using or disclosing your personal information except for the purpose of providing the Services.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>Protection of EDC and Others:</Bold> We may disclose the information we collect and maintain about you if
                required to do so by law or in a good faith belief that such access, retention or disclosure is reasonably
                necessary to: (a) comply with legal process (e.g., a subpoena or court order); (b) enforce this Privacy Notice; (c) respond to claims that any content
                violates the rights of third parties such as rights of privacy or intellectual property; (d) respond to your requests for customer service; and/or (e) protect the rights,
                property or personal safety of EDC, its agents and affiliates, its users and/or the public. This includes exchanging information with other companies and organizations for fraud protection, spam/malware prevention, and similar purposes.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>Your Employer and Users:</Bold> We may share your information with your employer, your employer’s other users
                of the Services and the EDC customer paying for your use of the Services.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>Consent:</Bold> We may share your information when we have your consent.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>For the Purpose for Which You Provide It:</Bold> We may share information you provide for the purpose for which
                you provide it.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>Intracompany:</Bold> We may share your information with any affiliates and subsidiaries to operate our business.
            </Typography>
            </ListItem>
            <ListItem>
            <Typography>
                <Bold>Enforcement:</Bold> To enforce our rights arising from any contracts entered into between you and us, including
                the <TermsLink /> and for billing and collection.
            </Typography>
            </ListItem>
        </List>
        </ContentSection>

        {/* Section 7: Retention of Your Personal Information */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            7. Retention of Your Personal Information
        </Typography>
        <Typography paragraph>
            We may retain your personal information both during and after the time you use our Services, but we keep your personal
            information for no longer than needed pursuant to a legitimate business purpose. For example, we may retain contact
            information after you register an account with us to maintain our internal financial or transactional records, to
            communicate with you about our relationship, or to deliver marketing communications to you in accordance with
            applicable law.
        </Typography>
        <Typography paragraph>
            The length of time for which we retain information depends on the purposes for which we collected and used it,
            contractual terms, requirements of applicable laws, the amount, nature, and sensitivity of the information, the potential risk of harm from
            unauthorized use or disclosure of the information, the resolution of any pending or threatened disputes, and
            enforcement of our agreements.
        </Typography>
        <Typography paragraph>
            We keep information collected on behalf of our Organization Customers for as long as necessary to fulfill the purpose for which it was collected, pursuant to contractual terms or as otherwise required by applicable law. When we no longer require the personal information, we will either delete or deidentify it, or if this is not possible,
            securely store it in accordance with this Privacy Notice and cease use of the personal information until deletion is
            possible. If we deidentify your personal information (so that it can no longer be associated with you and thus is no longer
            considered personal information under relevant laws), we may retain this information for longer periods.
        </Typography>
        </ContentSection>

        {/* Section 8: How We Protect Your Personal Information */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            8. How We Protect Your Personal Information
        </Typography>
        <Typography paragraph>
            We use physical, administrative, and technical security measures to protect the confidentiality, integrity, and availability
            of your information. The safety and security of your information also depends on you. Where we have given you (or
            where you have chosen) a password for access to certain parts of our Services, you are responsible for keeping this
            password confidential. Do not share your password with anyone.
        </Typography>
        <Typography paragraph>
            Unfortunately, the transmission of information via the internet is not completely secure. Although we use measures to
            protect your personal information, we cannot guarantee the security of your personal information transmitted to our
            Services.
        </Typography>
        </ContentSection>

        {/* Section 9: Rights and Choices */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            9. Rights and Choices
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Customer Data
        </Typography>
        <Typography paragraph>
            In response to a proper request from you regarding Customer Data received by EDC, we will direct you to contact the Organization Customer who provided the Customer Data to us. A proper request must include the name of the applicable Organization Customer, information we may require to verify your identity, and the specific right to be exercised.  If the Organization Customer is not responsive, and you notify us that the Organization Customer was not responsive and properly identify the Organization Customer, we will then notify the Organization Customer of your request and await guidance from the Organization Customer.  We will follow applicable law with respect to proper requests.  We will also reasonably cooperate and assist our Organization Customers in connection with access requests, inquiries, and complaints from data subjects to whom the Customer Data relates or from data protection authorities.  We will not directly respond to a request for Customer Data except on documented instructions of our Organization Customer.  EDC acknowledges that applicable law regarding data subject rights may be further promulgated, modified, or interpreted by supervisory authorities, and enforced by stakeholders such as state attorney generals.  EDC will reasonably cooperate and assist Organization Customers in responding to data subject requests.
        </Typography>
        <Typography paragraph>
            If you do not receive a response to your data request within the applicable legal time frame, or if you are not satisfied with the response provided by the Organization Customer, you have the right to lodge a complaint with the relevant data protection or supervisory authority in your jurisdiction.
        </Typography>

        <Typography paragraph>
            <Bold>Right to Know What Personal Information is Being Collected (Right to Access):</Bold> You have the right to request the following information:
        </Typography>
        <LetteredList>
            <LetteredItem>Categories of personal information collected about you.</LetteredItem>
            <LetteredItem>Categories of sources from which the personal information is collected.</LetteredItem>
            <LetteredItem>The business or commercial purpose for collecting, selling, or sharing your personal information.</LetteredItem>
            <LetteredItem>Categories of third parties to whom we disclose your personal information.</LetteredItem>
            <LetteredItem>The specific pieces of personal information we have collected about you.</LetteredItem>
        </LetteredList>

        <Typography paragraph>
            <Bold>Right to Delete Personal Information</Bold>: You have the right to request that we delete any personal information about you that we have collected from you.
        </Typography>

        <Typography paragraph>
            <Bold>Right to Correct Inaccurate Personal Information</Bold>: You have the right to request that we correct your inaccurate personal
            information if we maintain inaccurate personal information about you.
        </Typography>

        <Typography paragraph>
            <Bold>Right to Know What Personal Information is Sold or Shared and to Whom</Bold>: You have the right, if we sell or share your information, to request that we disclose to you the following:
        </Typography>
        <LetteredList>
            <LetteredItem>The categories of personal information that we collected from you.</LetteredItem>
            <LetteredItem>
                The categories of personal information that we sold or shared about you and the categories of personal
                information for each category of third parties to whom the personal information was sold or shared.
            </LetteredItem>
            <LetteredItem>
                The categories of personal information that we disclosed about you for a business purpose and the
                categories of persons to whom it was disclosed for a business purpose.
            </LetteredItem>
        </LetteredList>

        <Typography paragraph>
            <Bold>Right to Opt Out of Sale or Sharing of Personal Information</Bold>: You have the right to direct us, if we sell or share your personal information to third parties not to sell or share your 
            personal information. To exercise your opt-out right, please contact us at{' '}
            <StyledLink href="mailto:support@educationdatacollective.com">
            support@educationdatacollective.com
            </StyledLink>.
        </Typography>

        <Typography paragraph>
            <Bold>Right to Limit Use and Disclosure of Sensitive Personal Information</Bold>: You have the right to direct us to limit the use of your sensitive personal information to the use which is necessary to perform the Services.
        </Typography>

        <Typography paragraph>
            <Bold>Right of No Retaliation Following Opt Out or Exercise of Other Rights</Bold>: We may not discriminate against you if you exercise any of your rights as described above.
        </Typography>

        <Typography paragraph>
            <Bold>Right to Data Portability</Bold>: You have the right to obtain your personal information in a portable and, to the extent technically feasible, readily usable format that allows you to transmit the data to another entity without hindrance.
        </Typography>
        </ContentSection>

        {/* Section 10: Requests */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            10. Requests
        </Typography>
        <Typography paragraph>
            If you wish to exercise any of the rights described above, please submit a request. In your request, please specify which
            right you are seeking to exercise and the scope of the request. We may require specific information from you to help us
            verify your identity and process your request. If we are unable to verify your identity, we may deny your request. You can
            designate an authorized agent to submit requests on your behalf. However, we will require written proof of the agent’s
            permission to do so and verify your identity directly. Requests should be directed to{' '}
            <StyledLink href="mailto:support@educationdatacollective.com">
            support@educationdatacollective.com
            </StyledLink>.
        </Typography>
        <Typography paragraph>
            When you submit a request, do not send us, directly or indirectly, any sensitive or special categories of personal
            information (e.g., social security numbers or other national or state identifiers, health information, biometric data, and
            so on).
        </Typography>
        <Typography paragraph>
            We will evaluate and respond to your request to the extent required by law (or in our discretion if not required by law) in
            the time required by law and as permitted by our contracts, confidentiality obligations, and applicable laws and
            regulations. We may not be able to provide all of the information requested or fulfill your request due to certain
            exceptions enumerated under applicable law. In such a case, we will inform you of the reasons we cannot fulfill all or part
            of your request. If the request submission methods above do not enable you to submit your request, please contact us as set forth below and specify which right you wish to exercise.
        </Typography>
        </ContentSection>

        {/* Section 11: OPT-OUT Preference Signal */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            11. OPT-OUT Preference Signal
        </Typography>
        <Typography paragraph>
            If your browser supports an opt-out preference signal, you may be able to opt out of the sharing or sale of your personal
            information by some websites by properly configuring the opt-out preference signal. If you subsequently delete cookies,
            change your browser settings, or use a different browser, you will need to turn the opt-out signal on again. Our Services
            may not recognize or react in response to such signals. In the event our Services become subject to legal requirements to
            recognize opt-out signals, we will assess and provide an appropriate response to such signals.
        </Typography>
        </ContentSection>

        {/* Section 12: Children's Privacy */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            12. Children’s Privacy
        </Typography>
        <Typography paragraph>
            The Services are intended for general audiences and not for children under the age of 18. Under the Family Educational Rights and Privacy Act (FERPA), EDC acts as a “school official” with “legitimate educational interests” and contractually relies on Organization Customers and Customer Users to obtain parental consent, if required. Children under the age of 18 may not use the Services. Where the personal information of children under 18 is shared with us by an Organization Customer, we do not use such information for any purpose other than to provide our Services in accordance with contractual agreements with our Organization Customers and our <TermsLink />. If we become
            aware that we have inadvertently collected or received personal information from children under the age of 18 without
            valid parental consent, we will take reasonable steps to delete that information from our systems as soon as reasonably
            practicable.
        </Typography>
        </ContentSection>

        {/* Section 13: Third-Party Sites and Applications */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            13. Third-Party Sites and Applications
        </Typography>
        <Typography paragraph>
            Our Services may provide links to or make available integrations with third-party website or applications (“<Bold>Third-Party
            Site</Bold>”). Such Third-Party Sites are outside our control and not covered by this Privacy Notice. We are not liable for any
            information, content, products, services, software, or other materials available on or through Third-Party Sites. The use
            of a Third-Party Site or any information or other content found on a Third-Party Site is subject to and governed by the
            terms and conditions of such Third-Party Site. We encourage you to review the privacy notices posted on such
            Third-Party Site.
        </Typography>
        </ContentSection>

        {/* Section 14: Business Transfers */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            14. Business Transfers
        </Typography>
        <Typography paragraph>
            As we continue to develop our business, we may buy, merge, or partner with other entities. In such transactions user
            information may be among the transferred assets. If a portion or all of our assets are sold or transferred to a third party,
            user information (including information processed in accordance with this Privacy Notice) may be one of the transferred
            business assets. If such transfer is subject to additional mandatory restrictions under applicable laws, we will comply with
            such restrictions. Any third party to which we transfer or sell our assets will have the right to continue to use your
            information in the manner set out in this Privacy Notice.
        </Typography>
        </ContentSection>

        {/* Section 15: Changes to Our Privacy Notice */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            15. Changes to Our Privacy Notice
        </Typography>
        <Typography paragraph>
            We reserve the right to amend this Privacy Notice at any time to reflect changes in the law, our data collection and use
            practices, our Services, advances in technology, and for other purposes. We will make the revised Privacy Notice
            accessible through the Services and encourage you to review the Privacy Notice periodically. Any changes will be
            effective immediately upon the posting of the revised Privacy Notice. The “last updated” date included at the top of this
            Privacy Notice will indicate when it was last updated. If we make a material change to the Privacy Notice, you will be
            provided with appropriate notice by email or in accordance with other legal requirements. Your continued use of the
            Services following the notice of such changes indicates that you have read, understood, and agreed to be bound by the
            latest version of the new Privacy Notice. This Privacy Notice is not intended to, nor does it, create any contractual or legal
            right in or on behalf of any party, including you.
        </Typography>
        </ContentSection>

        {/* Section 16: Contact Information */}
        <ContentSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            16. Contact Information
        </Typography>
        <Typography paragraph>
            Please feel free to contact us if you have any questions about this Privacy Notice or our associated information practices.
            You may contact us as follows:
        </Typography>
        <Typography component="div">
            Education Data Collective, LLC<br />
            P.O. Box 7062, Denver, CO 80207<br />
            Email: <StyledLink href="mailto:support@educationdatacollective.com">
            support@educationdatacollective.com
            </StyledLink>
            </Typography>
        </ContentSection>
      </LegalDocLayout>
    <SiteFooter />
    </PageWrapper>
);
}

export default PrivacyNotice;
